import { gql, makeVar, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { EReactionType } from '@/graphql-types/globalTypes';
import { comment_liked } from '@/analytics';

import {
  useAutoLikeComment_createCommentReaction,
  useAutoLikeComment_createCommentReactionVariables,
} from './graphql-types/useAutoLikeComment_createCommentReaction';

const LIKE_COMMENT = gql`
  mutation useAutoLikeComment_createCommentReaction(
    $input: CreateCommentReactionInput!
  ) {
    createCommentReaction(input: $input) {
      data {
        _id
        reactionType
        comment {
          _id
          reactionCount
          currentUserReaction {
            _id
            reactionType
          }
          postV2 {
            _id
            tags {
              _id
            }
            announcement {
              _id
            }
            sponsorData {
              audience {
                group {
                  _id
                }
              }
            }
            group {
              _id
              isGlobalSingleton
            }
            organization {
              _id
            }
            event {
              _id
            }
          }
        }
      }
    }
  }
`;

const calledVar = makeVar(false);

export const useAutoLikeComment = (options: {
  validRoutes: string[];
}): void => {
  const [createLike] = useMutation<
    useAutoLikeComment_createCommentReaction,
    useAutoLikeComment_createCommentReactionVariables
  >(LIKE_COMMENT);
  const { validRoutes } = options;
  const router = useRouter();

  const toast = useToast();

  const isValidRoute = validRoutes.reduce(
    (prev, route) => prev || router.asPath.includes(route),
    false
  );
  React.useEffect(() => {
    const commentId = router.query.likeComment;
    if (
      !router.isReady ||
      !commentId ||
      !isValidRoute ||
      calledVar() ||
      typeof commentId !== 'string'
    ) {
      return;
    }
    calledVar(true);

    createLike({
      variables: {
        input: { commentId, reactionType: EReactionType.LIKE },
      },
    })
      .then((result) => {
        toast({
          title: `Successfully liked comment`,
          status: 'success',
        });
        const comment = result.data?.createCommentReaction.data?.comment;
        if (comment && comment.postV2) {
          comment_liked({
            entity: {
              id: comment._id,
              type: 'comment',
            },
            engagement: {
              reactionType: 'like',
            },
            parent: {
              id: comment.postV2._id,
              type: 'post',
            },
          });
        }
      })
      .catch(() =>
        toast({
          title: `Could not like comment`,
          status: 'error',
        })
      )
      .finally(() => {
        const { likeComment, ...queryWithoutLikeComment } = router.query;
        router.replace({ query: queryWithoutLikeComment });
      });
  }, [isValidRoute, createLike, toast, router]);
};
